import { CheckIcon } from '@radix-ui/react-icons'
import { InfoIcon } from 'lucide-react'
import React from 'react'

import { membersMarketplace } from 'rails_routes'
import { TextLink } from 'slab'
import { cn } from 'utils'
import {
  type MemberOnboardingMemberPlanChangesProps,
  type Money,
} from 'utils/web_api.schemas'

import { isCurrentPlanOffThatch, isNextPlanEmpty } from '../../utils'
import BudgetComparisonSection from '../BudgetComparisonSection/BudgetComparisonSection'
import MarketplaceProductSection from '../MarketplaceProductSection/MarketplaceProductSection'

interface BudgetAndPlanChangesSectionProps {
  condensed: boolean;
  planChanges: MemberOnboardingMemberPlanChangesProps;
  qmeEnabled: boolean;
  hasTieredAllowances: boolean;
  companyName: string;
}

interface BaseSectionProps {
  condensed: boolean;
  planChanges: MemberOnboardingMemberPlanChangesProps;
  maxAmountSubunits: number;
}

type MonthlyAllowanceSectionProps = BaseSectionProps & {
  companyName: string;
}
type MonthlyPremiumSectionProps = BaseSectionProps
type RemainderOrYouPaySectionProps = BaseSectionProps & {
  qmeEnabled: boolean;
  directionDifferent: boolean;
  remainingBalance: Money;
  remainingBalanceIsPositive: boolean;
}

export const MonthlyAllowanceSection = ({
  condensed,
  planChanges,
  maxAmountSubunits,
  companyName,
}: MonthlyAllowanceSectionProps) => {
  const allowanceChange = planChanges.year_over_year_change.allowance_amount
  let description = ''
  if (allowanceChange.subunits > 0) {
    description =
      (companyName || 'Your company') +
      ' is increasing your allowance to help offset the rising cost of insurance and ensure you continue to get great coverage.'
  } else if (allowanceChange.subunits < 0) {
    description =
      'Your allowance will be changing next year. If you want to find coverage that better fits your budget, you can shop for new plans.'
  } else {
    description =
      (companyName || 'Your company') +
      " is offering you the same allowance as last year to make sure you can keep your coverage if it's working for you."
  }

  return (
    <BudgetComparisonSection
      title='Monthly allowance'
      description={description}
      condensed={condensed}
      prevPlanYear={planChanges.current_plan_year.plan_year.toString()}
      currentPlanYear={planChanges.next_plan_year.plan_year.toString()}
      prevAmount={planChanges.current_plan_year.allowance_amount}
      currentAmount={planChanges.next_plan_year.allowance_amount}
      yoyChange={planChanges.year_over_year_change.allowance_amount}
      xAxisMaxAmountSubunits={maxAmountSubunits}
      increaseIsGreen
    />
  )
}

export const MonthlyPremiumSection = ({
  condensed,
  planChanges,
  maxAmountSubunits,
}: MonthlyPremiumSectionProps) => {
  const allowanceAmount = planChanges.next_plan_year.allowance_amount
  const premiumAmount = planChanges.next_plan_year.premium_total

  const premiumChange = planChanges.year_over_year_change.premium_total
  let description = ''

  const offThatchOrMedicare =
    isCurrentPlanOffThatch(planChanges) && isNextPlanEmpty(planChanges)
  // Handle the case where the plan is off thatch/medicare and there is no plan for the next year
  if (offThatchOrMedicare) {
    description =
      'Insurance rates generally increase every year due to factors like rising medical costs, prescription drug prices, ' +
      'economic inflation, and increased healthcare utilization. Here’s how much your plans increased compared to last year:'
  }

  // Now handle the case where the plan is not off thatch/medicare and there is a plan for the next year
  if (premiumChange.subunits > 0) {
    description =
      'Insurance rates generally increase every year due to factors like rising medical costs, ' +
      'prescription drug prices, economic inflation, and increased healthcare utilization.'
  } else if (premiumChange.subunits < 0) {
    description =
      'Insurance prices can sometimes go down in the individual market due to increased competition ' +
      'or if the insurance company has moved you to a slightly different plan. Lucky you!'
  } else {
    description =
      'Insurance prices can sometimes stay the same in the individual market due to ' +
      'increased competition and wider availability of plans. Lucky you!'
  }

  const showCoveredMessage =
    !offThatchOrMedicare && premiumAmount.subunits <= allowanceAmount.subunits

  return (
    <BudgetComparisonSection
      title='Total monthly insurance cost'
      description={description}
      condensed={condensed}
      prevPlanYear={planChanges.current_plan_year.plan_year.toString()}
      currentPlanYear={planChanges.next_plan_year.plan_year.toString()}
      prevAmount={planChanges.current_plan_year.premium_total}
      currentAmount={offThatchOrMedicare ? null : premiumAmount}
      yoyChange={
        offThatchOrMedicare
          ? null
          : planChanges.year_over_year_change.premium_total
      }
      xAxisMaxAmountSubunits={maxAmountSubunits}
      increaseIsGreen={false}
    >
      {showCoveredMessage && (
        <p className='flex items-center py-1 pl-2 pr-3 rounded-xl bg-green-50 mt-4 w-fit'>
          <CheckIcon className='h-5 w-5 text-green-400 mr-1' />
          <span className='body-sm text-gray-800'>
            You won't need to pay anything since your allowance covers your
            insurance cost
          </span>
        </p>
      )}
      {offThatchOrMedicare && (
        <p className='flex items-start py-1 pl-2 pr-3 rounded-xl bg-slate-50 mt-4 w-fit'>
          <InfoIcon size={16} className='h-5 w-5 text-slate-400 mr-1 shrink-0' />
          <span className='body-sm text-slate-500'>
            Since you purchased at least one plan outside of Thatch, we can't
            determine the cost
          </span>
        </p>
      )}
    </BudgetComparisonSection>
  )
}

const getRemainderSectionContent = ({
  directionDifferent,
  remainingBalanceIsPositive,
  remainingBalance,
  yoyChange,
}: {
  directionDifferent: boolean;
  remainingBalanceIsPositive: boolean;
  remainingBalance: Money;
  yoyChange: Money;
}) => {
  let title = ''
  let condensedTitle = ''
  let description = null

  if (remainingBalanceIsPositive) {
    title = 'Remainder added to your balance each month'
    condensedTitle = 'Remainder added to your balance'
  } else {
    title = 'Your monthly contribution'
    condensedTitle = 'Your monthly contribution'
  }

  if (remainingBalanceIsPositive) {
    if (yoyChange.subunits >= 0) {
      description = (
        <>
          You can spend your leftover balance on healthcare expenses like
          premiums, co-pays, or exclusive deals in the{' '}
          <TextLink href={membersMarketplace.index()} target='_blank'>
            Thatch marketplace
          </TextLink>
          .
        </>
      )
    } else {
      description = (
        <>
          Although insurance rates are rising next year, you'll still be saving{' '}
          {remainingBalance.formatted_amount.replace('-', '')} each month to
          spend on healthcare expenses like premiums, co-pays, or exclusive
          deals in the{' '}
          <TextLink href={membersMarketplace.index()} target='_blank'>
            Thatch marketplace
          </TextLink>
          .
        </>
      )
    }
  } else {
    if (remainingBalance.subunits === 0) {
      description =
        "Due to higher insurance rates this year, you won't accumulate any leftover funds from your allowance."
    } else if (directionDifferent) {
      description = `Due to higher rates, you'll have ${remainingBalance.formatted_amount.replace(
        '-',
        ''
      )} deducted from your paychecks in 2026 to cover the portion of your insurance costs not covered by your monthly allowance.`
    } else {
      description =
        'This amount will be deducted from your paychecks to cover the portion of your insurance costs not covered by your monthly allowance.'
    }
  }
  return { title, condensedTitle, description }
}

export const RemainderOrYouPaySection = ({
  condensed,
  planChanges,
  maxAmountSubunits,
  qmeEnabled,
  directionDifferent,
  remainingBalance,
  remainingBalanceIsPositive,
}: RemainderOrYouPaySectionProps) => {
  const yoyChange = planChanges.year_over_year_change.remaining_balance
  const offThatchOrMedicare =
    isCurrentPlanOffThatch(planChanges) && isNextPlanEmpty(planChanges)

  const { title, condensedTitle, description } = getRemainderSectionContent({
    directionDifferent,
    remainingBalanceIsPositive,
    remainingBalance,
    yoyChange,
  })

  return (
    <BudgetComparisonSection
      title={condensed ? condensedTitle : title}
      description={description}
      condensed={condensed}
      prevPlanYear={planChanges.current_plan_year.plan_year.toString()}
      currentPlanYear={planChanges.next_plan_year.plan_year.toString()}
      prevAmount={planChanges.current_plan_year.remaining_balance}
      currentAmount={
        offThatchOrMedicare
          ? null
          : planChanges.next_plan_year.remaining_balance
      }
      yoyChange={
        offThatchOrMedicare
          ? null
          : planChanges.year_over_year_change.remaining_balance
      }
      xAxisMaxAmountSubunits={maxAmountSubunits}
      increaseIsGreen={remainingBalanceIsPositive}
    >
      {remainingBalanceIsPositive && qmeEnabled && (
        <div className='mt-4'>
          <MarketplaceProductSection
            filterToCategories={[
              'Fitness',
              'Preventative care',
              'Sleep',
              'Mental health',
              'Telehealth',
            ]}
          />
        </div>
      )}
    </BudgetComparisonSection>
  )
}

const getMaxAmountAmongAllPlanChanges = (
  planChanges: MemberOnboardingMemberPlanChangesProps,
  directionDifferent: boolean
) => {
  if (directionDifferent) {
    const buffer = Math.abs(
      planChanges.current_plan_year.remaining_balance.subunits
    )
    return (
      Math.max(
        planChanges.current_plan_year.allowance_amount.subunits,
        planChanges.next_plan_year.allowance_amount.subunits,
        planChanges.current_plan_year.premium_total.subunits,
        planChanges.next_plan_year.premium_total.subunits,
        Math.abs(planChanges.next_plan_year.remaining_balance.subunits)
      ) + buffer
    )
  }

  return Math.max(
    planChanges.current_plan_year.allowance_amount.subunits,
    planChanges.next_plan_year.allowance_amount.subunits,
    planChanges.current_plan_year.premium_total.subunits,
    planChanges.next_plan_year.premium_total.subunits,
    Math.abs(planChanges.current_plan_year.remaining_balance.subunits),
    Math.abs(planChanges.next_plan_year.remaining_balance.subunits)
  )
}

const BudgetAndPlanChangesSection: React.FC<
  BudgetAndPlanChangesSectionProps
> = ({ condensed, planChanges, qmeEnabled, hasTieredAllowances, companyName }) => {
  const remainingBalance = planChanges.next_plan_year.remaining_balance
  const remainingBalanceIsPositive = remainingBalance.subunits > 0
  const directionDifferent =
    planChanges.current_plan_year.remaining_balance.subunits *
      remainingBalance.subunits <
    0
  const maxAmountSubunits = getMaxAmountAmongAllPlanChanges(
    planChanges,
    directionDifferent
  )
  const shouldNotRenderRemainderSection =
   (!qmeEnabled && remainingBalanceIsPositive) || hasTieredAllowances

  return (
    <div
      data-testid='budget-and-plan-changes-section'
      className={cn(
        'w-full border border-gray-200 rounded-2xl shadow-sm transition-all duration-700 overflow-hidden grid',
        {
          'gap-y-10 p-6 grid-cols-1': !condensed,
          'grid-rows-[auto_auto]':
              !condensed && shouldNotRenderRemainderSection,
          'grid-rows-[auto_auto_auto]':
              !condensed && !shouldNotRenderRemainderSection,
          'gap-x-4 p-4 grid-rows-1 grid-cols-3 bg-white shadow-lg': condensed,
        }
      )}
    >
      <MonthlyAllowanceSection
        condensed={condensed}
        companyName={companyName}
        planChanges={planChanges}
        maxAmountSubunits={maxAmountSubunits}
      />
      <MonthlyPremiumSection
        condensed={condensed}
        planChanges={planChanges}
        maxAmountSubunits={maxAmountSubunits}
      />
      {!shouldNotRenderRemainderSection
        ? (
          <RemainderOrYouPaySection
            condensed={condensed}
            remainingBalance={remainingBalance}
            remainingBalanceIsPositive={remainingBalanceIsPositive}
            planChanges={planChanges}
            maxAmountSubunits={maxAmountSubunits}
            qmeEnabled={qmeEnabled}
            directionDifferent={directionDifferent}
          />
          )
        : condensed
          ? (
            <div />
            )
          : null}
    </div>
  )
}

export default BudgetAndPlanChangesSection
